/**
 * Generic Entity Permissions Hook
 *
 * Core implementation for converting async permission checks to synchronous boolean state.
 * This hook is NOT meant to be used directly - use typed wrappers instead:
 * - useProjectPermissions
 * - useUserPermissions
 * - useCompanyPermissions
 *
 * Architecture:
 * - Executes all permission checks in parallel via Promise.all
 * - Properly handles cleanup with cancellation tokens
 * - Provides loading and error states
 * - Re-evaluates when dependencies change
 */

import { useState, useEffect } from 'react';
import { componentLogger } from '@/lib/logging';

/**
 * Configuration for a single permission check
 */
interface PermissionConfig {
  fn: (..._args: unknown[]) => Promise<boolean>;
  args: unknown[];
}

/**
 * Map of permission names to their configuration
 */
interface PermissionConfigMap {
  [key: string]: PermissionConfig;
}

/**
 * Result of useEntityPermissions hook
 */
interface UseEntityPermissionsResult<T extends Record<string, boolean>> {
  permissions: T;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Generic hook for converting async permission checks to sync boolean state.
 *
 * @param config - Map of permission names to async permission functions
 * @param dependencies - Dependencies that trigger re-evaluation
 * @returns Object containing permissions, loading state, and error
 *
 * @example
 * ```typescript
 * const result = useEntityPermissions<ProjectPermissions>(
 *   {
 *     canEditProject: { fn: canEditProject, args: [projectId, status] },
 *     canDeleteProject: { fn: canDeleteProject, args: [projectId] },
 *   },
 *   [projectId, status]
 * );
 * ```
 */
export function useEntityPermissions<T extends Record<string, boolean>>(
  config: PermissionConfigMap,
  dependencies: unknown[]
): UseEntityPermissionsResult<T> {
  const [state, setState] = useState<UseEntityPermissionsResult<T>>({
    permissions: {} as T,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    const loadPermissions = async () => {
      // Only show loading state if we don't have permissions yet
      // This prevents flickering on re-evaluation
      setState(prev => ({
        ...prev,
        isLoading: Object.keys(prev.permissions).length === 0,
        error: null
      }));

      try {
        // Convert config to array of entries for iteration
        const entries = Object.entries(config);

        // Check if permission checks should be skipped
        // Skip only if ALL non-empty arg arrays contain ONLY invalid values
        // Empty arg arrays are VALID (functions may use closure variables)
        const shouldSkipEvaluation = entries.every(([_, { args }]) => {
          // Empty args array is valid - function uses closure variables
          if (args.length === 0) return false;

          // Non-empty args: check if all values are invalid
          return args.every(arg => arg === undefined || arg === null || arg === '');
        });

        if (shouldSkipEvaluation) {
          // No valid entity context - return all false
          const allFalsePermissions = entries.reduce((acc, [key]) => {
            acc[key] = false;
            return acc;
          }, {} as Record<string, boolean>);

          if (!cancelled) {
            setState({
              permissions: allFalsePermissions as T,
              isLoading: false,
              error: null,
            });
          }
          return;
        }

        // Execute all permission checks in parallel
        const results = await Promise.all(
          entries.map(([_, { fn, args }]) => fn(...args))
        );

        // Convert results array back to object with permission names
        const permissions = entries.reduce((acc, [key], index) => {
          acc[key] = results[index] ?? false; // Fallback to false if undefined
          return acc;
        }, {} as Record<string, boolean>);

        if (!cancelled) {
          setState({
            permissions: permissions as T,
            isLoading: false,
            error: null,
          });
        }
      } catch (error) {
        componentLogger.error('useEntityPermissions', 'Permission evaluation failed', error);

        if (!cancelled) {
          // On error, set all permissions to false (fail-secure)
          const allFalsePermissions = Object.keys(config).reduce((acc, key) => {
            acc[key] = false;
            return acc;
          }, {} as Record<string, boolean>);

          setState({
            permissions: allFalsePermissions as T,
            isLoading: false,
            error: error as Error,
          });
        }
      }
    };

    loadPermissions();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return state;
}
